import React from 'react';
import { Globe, IndianRupee, CheckCircle2, AlertTriangle, AlertCircle } from 'lucide-react';

export default function StatsOverview({ stats, activeFilter, onFilterChange }) {
  const {
    totalDomains = 0,
    expiredCount = 0,
    urgentCount = 0,
    expiringSoonCount = 0,
    activeCount = 0,
    totalAnnualCost = 0
  } = stats || {};

  const expiringTotal = urgentCount + expiringSoonCount + expiredCount;

  const cards = [
    {
      id: 'all',
      title: 'Total Managed Domains',
      value: totalDomains,
      subtitle: `${activeCount} Active & Healthy`,
      icon: Globe,
      color: '#4f46e5',
      bgGradient: 'linear-gradient(135deg, #ffffff 0%, #eef2ff 100%)',
      borderColor: '#c7d2fe',
      iconBg: 'linear-gradient(135deg, #818cf8 0%, #4f46e5 100%)'
    },
    {
      id: 'needs_renewal',
      title: 'Renewal Needed',
      value: expiringTotal,
      subtitle: `${urgentCount} Urgent, ${expiringSoonCount} Expiring Soon, ${expiredCount} Expired`,
      icon: AlertTriangle,
      color: '#d97706',
      bgGradient: 'linear-gradient(135deg, #ffffff 0%, #fffbeb 100%)',
      borderColor: '#fde68a',
      iconBg: 'linear-gradient(135deg, #fbbf24 0%, #d97706 100%)',
      alert: expiringTotal > 0
    },
    {
      id: 'expired',
      title: 'Expired Domains',
      value: expiredCount,
      subtitle: expiredCount > 0 ? 'Requires Immediate Renewal!' : 'No Expired Domains',
      icon: AlertCircle,
      color: '#dc2626',
      bgGradient: 'linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)',
      borderColor: '#fecaca',
      iconBg: 'linear-gradient(135deg, #f87171 0%, #dc2626 100%)',
      alert: expiredCount > 0
    },

    {
      id: 'cost',
      title: 'Annual Expense',
      value: `₹${totalAnnualCost.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      subtitle: 'Estimated Yearly Renewal',
      icon: IndianRupee,
      color: '#059669',
      bgGradient: 'linear-gradient(135deg, #ffffff 0%, #ecfdf5 100%)',
      borderColor: '#a7f3d0',
      iconBg: 'linear-gradient(135deg, #34d399 0%, #059669 100%)'
    }
  ];

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
      gap: '18px',
      marginBottom: '28px'
    }}>
      {cards.map(card => {
        const Icon = card.icon;
        const isSelected = activeFilter === card.id;

        return (
          <div
            key={card.id}
            onClick={() => {
              if (card.id === 'cost') return;
              onFilterChange(isSelected ? 'all' : card.id);
            }}
            className="glass-panel glass-panel-interactive"
            style={{
              padding: '22px',
              cursor: card.id !== 'cost' ? 'pointer' : 'default',
              borderColor: isSelected ? card.color : card.borderColor,
              background: card.bgGradient,
              boxShadow: isSelected ? `0 8px 25px -5px ${card.borderColor}` : '0 4px 15px -3px rgba(0, 0, 0, 0.04)',
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '20px'
            }}
          >
            {/* Top Accent Strip */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: card.iconBg
            }} />

            {/* Top Row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
              <span style={{ fontSize: '0.86rem', fontWeight: '700', color: 'var(--text-muted)' }}>
                {card.title}
              </span>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                background: card.iconBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)'
              }}>
                <Icon size={20} color="#ffffff" />
              </div>
            </div>

            {/* Metric Value */}
            <div style={{ fontSize: '1.9rem', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.5px', marginBottom: '4px' }}>
              {card.value}
            </div>

            {/* Subtitle */}
            <div style={{
              fontSize: '0.8rem',
              fontWeight: '600',
              color: card.alert ? card.color : 'var(--text-dim)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              {card.id === 'all' && <CheckCircle2 size={14} color="#059669" />}
              {card.subtitle}
            </div>
          </div>
        );
      })}
    </div>
  );
}
